import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import dayjs from '../../utils/dayjs';
import { CalendarEvent } from './types';
import CalendarDayCell from './CalendarDayCell';
import { getCalendarUiPalette } from './calendarPalette';

interface CalendarMonthGridProps {
  currMonth: string;
  selectedDate: Date;
  eventsByDay: Record<string, CalendarEvent[]>;
  headerContent?: React.ReactNode;
  onSelectDate: (date: Date) => void;
  onSelectMore: (date: Date) => void;
  onSelectEvent: (event: CalendarEvent) => void;
  onDoubleClickEvent: (event: CalendarEvent) => void;
}

const weekdayLabels = Array.from({ length: 7 }, (_, index) => dayjs().day(index).format('ddd').toUpperCase());

function buildCalendarDays(currMonth: string) {
  const startOfMonth = dayjs(`${currMonth}-01`).startOf('month');
  const endOfMonth = startOfMonth.endOf('month');
  const gridStart = startOfMonth.startOf('week');
  const gridEnd = endOfMonth.endOf('week');
  const days: Date[] = [];

  let cursor = gridStart;
  while (cursor.isBefore(gridEnd, 'day') || cursor.isSame(gridEnd, 'day')) {
    days.push(cursor.toDate());
    cursor = cursor.add(1, 'day');
  }

  return days;
}

export default function CalendarMonthGrid({
  currMonth,
  selectedDate,
  eventsByDay,
  headerContent,
  onSelectDate,
  onSelectMore,
  onSelectEvent,
  onDoubleClickEvent,
}: Readonly<CalendarMonthGridProps>) {
  const theme = useTheme();
  const calendarUiPalette = getCalendarUiPalette(theme);
  const days = buildCalendarDays(currMonth);
  const monthValue = dayjs(`${currMonth}-01`);

  return (
    <Box
      sx={{
        width: '100%',
        background: calendarUiPalette.monthGrid.background,
        padding: '1.25rem',
        borderRadius: '12px',
      }}
    >
      {headerContent ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mt: 1,
            mb: 4,
            minWidth: 0,
          }}
        >
          <Box sx={{ flex: '1 1 auto' }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0, maxWidth: '100%' }}>{headerContent}</Box>
        </Box>
      ) : null}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: 1.5,
          mb: 1.5,
        }}
      >
        {weekdayLabels.map((label) => (
          <Typography
            key={label}
            component="div"
            sx={{
              pb: 0.5,
              color: calendarUiPalette.categoryBar.headingText,
              fontFamily: '"Open Sans", Arial, sans-serif',
              fontSize: '0.7rem',
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              textAlign: 'center',
            }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: 1.5,
        }}
      >
        {days.map((date) => {
          const dayKey = dayjs(date).format('YYYY-MM-DD');

          return (
            <CalendarDayCell
              key={dayKey}
              date={date}
              isCurrentMonth={dayjs(date).isSame(monthValue, 'month')}
              isSelected={dayjs(date).isSame(selectedDate, 'day')}
              isToday={dayjs(date).isSame(dayjs(), 'day')}
              events={eventsByDay[dayKey] ?? []}
              onSelectDate={onSelectDate}
              onSelectMore={onSelectMore}
              onSelectEvent={onSelectEvent}
              onDoubleClickEvent={onDoubleClickEvent}
            />
          );
        })}
      </Box>
    </Box>
  );
}
